import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChoiceField } from "./ChoiceField";
import { typography, textColors } from "../../../design-system/tokens";
import InfoIcon from "../InfoIcon";
import ScreenSizeModeSelector from "./ScreenSizeModeSelector";

/**
 * ByLayoutChoiceField — breakpoint-aware wrapper around ``ChoiceField``.
 *
 * Used by responsive OneToOne wrappers whose per-breakpoint
 * children are simple ``CharField(choices=…)`` controls — currently
 * ``ResponsiveButtonCollectionAlignment`` (start / center / end /
 * full). Mirrors the ``ByLayoutSpacingField`` /
 * ``ByLayoutTextPositionField`` editing affordance so every
 * responsive wrapper in the editor offers the same one / two / all
 * editing modes.
 *
 * The inner ``ChoiceField`` already segments cleanly when the
 * choice count is ≤ 4 (which covers every alignment dimension we
 * currently ship), so the wrapper just needs to render it per
 * breakpoint and route updates through the canonical XS..XL keys.
 */

const BREAKPOINT_KEYS = ["extra_small", "small", "medium", "large", "extra_large"];

function deriveTwoMode(values) {
  const xs = values?.extra_small;
  const s = values?.small ?? xs;
  const m = values?.medium;
  const l = values?.large ?? m;
  const xl = values?.extra_large ?? l;
  const xsEqual = xs && s && xs === s;
  const mEqual = m && l && xl && m === l && l === xl;
  return {
    isTwo: Boolean(xsEqual && mEqual && xs !== m),
    vMobile: xs || s || m,
    vDesktop: m || l || xl,
  };
}

function allEqual(values) {
  const first = values?.extra_small;
  if (!first) return false;
  return BREAKPOINT_KEYS.every((k) => values?.[k] === first);
}

function pickFallback(values) {
  for (const k of BREAKPOINT_KEYS) {
    if (values?.[k]) return values[k];
  }
  return undefined;
}

export default function ByLayoutChoiceField({
  label,
  help,
  value,
  onChange,
  choices = [],
  infoText,
}) {
  const { isTwo, vMobile, vDesktop } = useMemo(
    () => deriveTwoMode(value || {}),
    [value]
  );
  const [mode, setMode] = useState(
    allEqual(value || {}) ? "one" : isTwo ? "two" : "all"
  );
  const modeTouchedRef = useRef(false);

  useEffect(() => {
    if (modeTouchedRef.current) return;
    const next = allEqual(value || {})
      ? "one"
      : deriveTwoMode(value || {}).isTwo
      ? "two"
      : "all";
    setMode(next);
  }, [value]);

  const formattedLabel =
    typeof label === "string" && label.length > 0
      ? label.charAt(0).toUpperCase() + label.slice(1)
      : label;

  const setUnified = useCallback(
    (next) => {
      onChange({
        extra_small: next,
        small: next,
        medium: next,
        large: next,
        extra_large: next,
      });
      setMode("one");
    },
    [onChange]
  );

  const applyMobile = useCallback(
    (next) => {
      onChange({ extra_small: next, small: next });
      setMode("two");
    },
    [onChange]
  );

  const applyDesktop = useCallback(
    (next) => {
      onChange({ medium: next, large: next, extra_large: next });
      setMode("two");
    },
    [onChange]
  );

  return (
    <div className="field-wrapper">
      {label && (
        <label
          className={`block mb-2 ${typography.label} ${textColors.secondary}`}
        >
          {formattedLabel}
          {infoText ? (
            <span className="ml-2 align-middle">
              <InfoIcon text={infoText} />
            </span>
          ) : null}
        </label>
      )}
      {help && (
        <div className={`mb-2 ${typography.caption} ${textColors.tertiary}`}>
          {help}
        </div>
      )}
      <ScreenSizeModeSelector
        mode={mode}
        onChange={(newMode) => {
          modeTouchedRef.current = true;
          setMode(newMode);
        }}
        disabled={false}
      />

      {mode === "one" ? (
        <ChoiceField
          label=""
          help={null}
          value={pickFallback(value)}
          onChange={setUnified}
          choices={choices}
        />
      ) : mode === "two" ? (
        <div className="grid grid-cols-1 gap-3">
          <ChoiceField
            label="Phone"
            help={null}
            value={vMobile}
            onChange={applyMobile}
            choices={choices}
          />
          <ChoiceField
            label="Desktop"
            help={null}
            value={vDesktop}
            onChange={applyDesktop}
            choices={choices}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          <ChoiceField
            label="Phone"
            help={null}
            value={value?.extra_small}
            onChange={(v) => onChange({ extra_small: v })}
            choices={choices}
          />
          <ChoiceField
            label="Tablet"
            help={null}
            value={value?.small ?? value?.extra_small}
            onChange={(v) => onChange({ small: v })}
            choices={choices}
          />
          <ChoiceField
            label="Laptop"
            help={null}
            value={value?.medium}
            onChange={(v) => onChange({ medium: v })}
            choices={choices}
          />
          <ChoiceField
            label="Desktop"
            help={null}
            value={value?.large ?? value?.medium}
            onChange={(v) => onChange({ large: v })}
            choices={choices}
          />
          <ChoiceField
            label="Desk XL"
            help={null}
            value={value?.extra_large ?? value?.large}
            onChange={(v) => onChange({ extra_large: v })}
            choices={choices}
          />
        </div>
      )}
    </div>
  );
}
